/**
 * Column vocabulary for one import type, plus the header auto-mapping it
 * drives. Keeping the vocabulary in one object means the CSV template, the
 * XLSX template and the mapping dropdown can never drift apart — they are
 * all derived from `headerMap`.
 */

export interface ColumnSchema {
  /**
   * Canonical kebab-case template header → domain field name. Key order
   * defines the template's column order, so it must match the example rows
   * cell-for-cell.
   */
  headerMap: Record<string, string>;
  /** Canonical header → human label shown in the mapping dropdown. */
  headerLabel: Record<string, string>;
  /** Lower-cased alias found in the wild → canonical header. */
  headerAliases: Record<string, string>;
  /** Canonical headers that must be mapped before the user can continue. */
  requiredHeaders: readonly string[];
}

/** Template columns in order. */
export function templateHeaders(schema: ColumnSchema): string[] {
  return Object.keys(schema.headerMap);
}

/** Best-effort auto-map: exact template hit → alias hit → unmapped (""). */
export function autoMapHeaders(
  schema: ColumnSchema,
  fileHeaders: readonly string[],
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const h of fileHeaders) {
    const norm = h.trim().toLowerCase();
    if (schema.headerMap[norm]) {
      map[h] = norm;
      continue;
    }
    const alias = schema.headerAliases[norm];
    map[h] = alias ?? "";
  }
  return map;
}

/** True once every required column has some file column pointing at it. */
export function mappingSatisfied(schema: ColumnSchema, map: Record<string, string>): boolean {
  const mapped = new Set(Object.values(map).filter(Boolean));
  return schema.requiredHeaders.every((r) => mapped.has(r));
}

/**
 * Rewrites raw file rows so their keys are canonical template headers.
 * Unmapped file columns are dropped.
 */
export function applyMapping(
  data: ReadonlyArray<Record<string, string>>,
  mapping: Record<string, string>,
): Array<Record<string, string>> {
  return data.map((raw) => {
    const out: Record<string, string> = {};
    for (const [fileHeader, canonical] of Object.entries(mapping)) {
      if (canonical) out[canonical] = raw[fileHeader] ?? "";
    }
    return out;
  });
}
